import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'react-bootstrap-icons';
import { getImageSrc } from '../../utils/imageSrc';

/**
 * Просмотрщик фото завода — МСК Газобетон.
 *
 * Свой, а не общий NomenclatureImagesModal: тот используют таблицы производств
 * на всех направлениях, и кнопка «Скопировать фото» появилась бы там, где её не
 * просили. Разметка и поведение повторяют общий просмотрщик намеренно — менеджер
 * открывает фото из двух мест и не должен переучиваться.
 *
 * Копирование нужно для переписки с клиентом: менеджер вставляет снимок прямо в
 * мессенджер. Буфер принимает только PNG, поэтому JPEG с Диска сначала
 * перерисовывается в canvas. Браузер может запретить запись в буфер (нет
 * разрешения, старая версия, страница не в фокусе) — тогда вместо тишины
 * показывается, что делать руками.
 */

const COPY_IDLE = 'idle';
const COPY_BUSY = 'busy';
const COPY_DONE = 'done';
const COPY_FAILED = 'failed';

// Сколько держится подпись «Скопировано» на кнопке.
const COPY_NOTICE_MS = 2000;

const copyImageToClipboard = async (src) => {
  if (!navigator.clipboard || typeof window.ClipboardItem !== 'function') {
    throw new Error('Буфер обмена недоступен');
  }

  // cache: 'reload' обязателен. Эту же картинку уже загрузил <img> запросом без
  // CORS, и браузер переиспользует тот ответ для fetch — а он без заголовка
  // Access-Control-Allow-Origin, и копирование падает на ровном месте. Обход
  // кеша заставляет сходить в сеть и получить ответ с нужным заголовком.
  const response = await fetch(src, { credentials: 'include', cache: 'reload' });
  const blob = await response.blob();

  // Буфер обмена браузера принимает PNG; картинки с Диска приходят JPEG.
  if (blob.type === 'image/png') {
    await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
    return;
  }

  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d').drawImage(bitmap, 0, 0);

  const png = await new Promise((resolve, reject) => {
    canvas.toBlob((result) => (result ? resolve(result) : reject(new Error('Не удалось перерисовать'))), 'image/png');
  });

  await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': png })]);
};

/**
 * @param {object}   props
 * @param {boolean}  props.isOpen
 * @param {string}   [props.title]     — название завода в заголовке
 * @param {string[]} props.imageUrls   — ссылки как они лежат в справочнике
 * @param {boolean}  [props.isLoading] — список ещё перечитывается
 * @param {function} props.onClose
 */
const GazPhotoViewer = ({ isOpen, title, imageUrls, isLoading = false, onClose }) => {
  const images = Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : [];
  const hasMany = images.length > 1;

  const [index, setIndex] = useState(0);
  const [copyState, setCopyState] = useState(COPY_IDLE);
  const copyTimerRef = useRef(null);

  const showPrevious = useCallback(() => {
    setIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const showNext = useCallback(() => {
    setIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  // Открыли заново — показываем первое фото и убираем подпись о копировании.
  useEffect(() => {
    if (isOpen) {
      setIndex(0);
      setCopyState(COPY_IDLE);
    }
  }, [isOpen, imageUrls]);

  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (!hasMany) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        showPrevious();
      }
      if (event.key === 'ArrowRight') {
        showNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasMany, isOpen, onClose, showNext, showPrevious]);

  if (!isOpen) {
    return null;
  }

  const currentUrl = images[index];

  const handleCopy = async () => {
    clearTimeout(copyTimerRef.current);
    // Снимок с Диска — несколько мегабайт, и перерисовка в PNG занимает пару
    // секунд. Без этого состояния кнопка всё это время молчит, и менеджер жмёт
    // её второй раз.
    setCopyState(COPY_BUSY);

    try {
      await copyImageToClipboard(getImageSrc(currentUrl));
      setCopyState(COPY_DONE);
    } catch (error) {
      setCopyState(COPY_FAILED);
    }

    copyTimerRef.current = setTimeout(() => setCopyState(COPY_IDLE), COPY_NOTICE_MS);
  };

  const COPY_LABELS = {
    [COPY_BUSY]: 'Копирую…',
    [COPY_DONE]: 'Скопировано ✓',
  };
  const copyLabel = COPY_LABELS[copyState] || 'Скопировать фото';

  const content = (
    <div className="gaz-photo" onClick={onClose} role="presentation">
      <div className="gaz-photo__box" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="gaz-photo__close" aria-label="Закрыть" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="gaz-photo__title">{title || 'Фото газобетона'}</div>

        {isLoading ? (
          <div className="gaz-photo__stub">Загрузка…</div>
        ) : currentUrl ? (
          <>
            <div className="gaz-photo__stage">
              {hasMany && (
                <button
                  type="button"
                  className="gaz-photo__arrow gaz-photo__arrow--prev"
                  aria-label="Предыдущее фото"
                  onClick={showPrevious}
                >
                  <ChevronLeft size={22} />
                </button>
              )}

              <img className="gaz-photo__image" src={getImageSrc(currentUrl)} alt={title || 'Фото газобетона'} />

              {hasMany && (
                <button
                  type="button"
                  className="gaz-photo__arrow gaz-photo__arrow--next"
                  aria-label="Следующее фото"
                  onClick={showNext}
                >
                  <ChevronRight size={22} />
                </button>
              )}
            </div>

            <div className="gaz-photo__actions">
              <button
                type="button"
                className="gaz-photo__copy"
                onClick={handleCopy}
                disabled={copyState === COPY_BUSY}
              >
                {copyLabel}
              </button>
              {copyState === COPY_FAILED && (
                <span className="gaz-photo__copy-error">
                  Не удалось скопировать, сохраните фото правой кнопкой
                </span>
              )}
            </div>

            {hasMany && (
              <div className="gaz-photo__strip">
                {images.map((url, position) => (
                  <button
                    type="button"
                    key={`${url}-${position}`}
                    className={`gaz-photo__thumb${position === index ? ' is-current' : ''}`}
                    aria-label={`Фото ${position + 1}`}
                    onClick={() => setIndex(position)}
                  >
                    <img src={getImageSrc(url)} alt="" />
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="gaz-photo__stub">Фото не найдено</div>
        )}
      </div>
    </div>
  );

  // Портал — внутрь корневой обёртки редизайна, а не в body: токены цветов
  // объявлены на .app-redesign, и за её пределами просмотрщик остался бы без
  // палитры и без переключения темы. Если обёртки нет — body как запасной
  // вариант, лучше без темы, чем без просмотрщика.
  return createPortal(content, document.querySelector('.app-redesign') || document.body);
};

export default GazPhotoViewer;
